import { useState, useEffect } from "react"
import Text from "@mobi/ds/components/Text"

import IAccordionProps, {
  IAccordionSingleProps,
  IAccordionMultipleProps,
} from "./types"
import { useAccordionSingle, useAccordionMultiple } from "./hooks"
const ArrowIcon = require("../../static/icons/up-arrow-orange.svg").default
import "./Accordion.scss"

const createMarkup = (data) => ({
  __html: data,
})

const Accordion = ({
  title,
  children,
  initialOpen = false,
  onClick = null,
  onOpen = null,
  onClose = null,
  items = null,
  index,
  oneAtATime = false,
  ...props
}: IAccordionProps) => {
  const [single, setSingle] = useState(null)
  const [AccordionComponent, setAccordionComponent] = useState(null)

  const defineComponent = (): void => {
    if (single) {
      setAccordionComponent(
        <Single
          title={title}
          children={children}
          initialOpen={initialOpen}
          onClick={onClick}
          onOpen={onOpen}
          onClose={onClose}
          index={index}
          {...props}
        />
      )
      return
    }
    setAccordionComponent(
      <Multiple items={items} onClick={onClick} {...props} />
    )
  }

  useEffect((): void => {
    setSingle(!items || items.length === 0)
  }, [])

  useEffect((): void => {
    if (single === null) return
    defineComponent()
  }, [single])

  const Single = ({
    title,
    children,
    initialOpen,
    onClick,
    onOpen,
    onClose,
    index: propIndex,
    open = null,
    tagTitle = "span",
    ...props
  }: IAccordionSingleProps) => {
    const { isOpen, itemId, panelId, index, handleClick } = useAccordionSingle({
      single,
      initialOpen,
      open,
      propIndex,
      onClick,
      onOpen,
      onClose,
    })

    return (
      <div
        className={`ds-accordion ${isOpen ? "ds-accordion--open" : ""}`}
        {...props}
      >
        <button
          id={itemId}
          aria-controls={panelId}
          aria-expanded={!!isOpen}
          onClick={() => handleClick(index)}
          className="ds-accordion__topic"
          type="button"
        >
          <div className="ds-accordion__topic__text-container">
            <Text as={tagTitle} className="ds-accordion__topic__text-title">
              {title}
            </Text>
          </div>
          <div className="ds-accordion__topic__icon-container">
            <ArrowIcon aria-hidden="true" className="ds-accordion__arrow" />
          </div>
        </button>
        <div
          id={panelId}
          aria-labelledby={itemId}
          aria-hidden={!isOpen}
          className="ds-accordion__content"
          {...(typeof children === "string"
            ? { dangerouslySetInnerHTML: createMarkup(children) }
            : { children })}
        />
      </div>
    )
  }

  const Multiple = ({ items, onClick, ...props }: IAccordionMultipleProps) => {
    const { actives, handleClick } = useAccordionMultiple({
      items,
      oneAtATime,
      onClick,
    })

    return items.map(
      (
        { title, children, initialOpen, onClick, onOpen, onClose, index },
        i
      ) => (
        <Single
          title={title}
          children={children}
          initialOpen={initialOpen}
          onClick={() => handleClick(onClick, index)}
          onOpen={onOpen}
          onClose={onClose}
          index={index ?? i + 1}
          key={i}
          open={actives.includes(index)}
          {...props}
        />
      )
    )
  }

  if (single === null || AccordionComponent === null) {
    return <></>
  }

  return AccordionComponent
}

export default Accordion
